import type { FunctionComponent } from 'react'
import React from 'react'
import { Field, FormModal } from '@ztechapps/webui.form'
import { Col, Row } from 'react-bootstrap'
import { PhoneNumberInput } from '@ztechapps/webui.input'
import { buildGraphqlQuery, isValidEmail, validatePhoneNumber } from '@ztechapps/common.utils'
import { useMutation } from '@ztechapps/common.hook'
import type { User } from '@ztechapps/common.contract'
import isEqual from 'lodash/isEqual'
import { WorldTimeZoneSelect } from '@ztechapps/webui.location'
import type { PartialDeep } from 'type-fest'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { BusyButtonLink } from '@ztechapps/webui.button'
import type { CompanySelectProps } from '@ztechapps/webui.contract'
import { useInjectComponent, WebuiIocKey } from '@ztechapps/webui.contract'

const userFragment = `_id profile { _id email picture name } phone timeZone company { _id name }`
const userQuery = buildGraphqlQuery('User', userFragment)

type TimeZone = {
  value: string
}
type UserForm = {
  _id: string
  profile: {
    name: string
    picture: string
    email: string
  }
  phone?: string
  timeZone?: TimeZone | string
  company?: { _id: string; name: string; __isCreate?: boolean } | null
}

export const UpdateProfile = (props: User & { trigger?: FunctionComponent<ModalTriggerProps> }) => {
  const [onUpdateUser] = useMutation({ ...userQuery.updateConfig })
  const CompanySelectComponent = useInjectComponent<CompanySelectProps>(WebuiIocKey.company.companySelect)

  const trigger =
    props?.trigger ??
    (({ onClick }) => <BusyButtonLink onClick={onClick} icon="edit" title="Edit this user profile?" />)

  return (
    <FormModal
      {...{
        trigger,
        initialValues: props,
        onSubmit: async (value: UserForm) => {
          if (
            !isEqual(value?.profile, props?.profile) ||
            !isEqual(value?.phone, props?.phone) ||
            !isEqual(value?.timeZone, props?.timeZone) ||
            !isEqual(value?.company, props?.company)
          ) {
            const { _id, profile, phone, timeZone, company } = value

            const companyPayload = (() => {
              if (!company) return { company: null }
              if (company.__isCreate) return { company: { name: company.name } }
              return { company: { _id: company._id } }
            })()

            await onUpdateUser({
              input: {
                _id,
                profile,
                phone,
                'services.basic.name': profile?.name,
                'services.basic.email': profile?.email,
                'services.basic.username': profile?.email,
                timeZone: typeof timeZone === 'string' ? timeZone : timeZone?.value,
                ...companyPayload,
              },
            })
          }
        },
        title: 'Edit user profile',
        saveText: 'Save',
        validate: (value: User) => {
          const error: PartialDeep<UserForm> = {}
          if (value?.phone) {
            try {
              validatePhoneNumber(value?.phone)
            } catch (e) {
              error.phone = e.message
            }
          }
          if (value.profile?.email) {
            const emailIsValid = isValidEmail(value.profile?.email)
            if (!emailIsValid) {
              error.profile = {
                email: 'Invalid email format',
              }
            }
          }

          if (!value.timeZone) {
            error.timeZone = 'Required'
          }

          return error
        },
      }}
    >
      <Row className="gy-3">
        <Col md={4}>
          <Field
            {...{
              label: 'Name',
              name: 'profile.name',
              help: 'The user profile name',
            }}
          />
        </Col>
        <Col md={4}>
          <Field
            {...{
              label: 'Email',
              name: 'profile.email',
              help: 'The user profile email. Also the email used to login',
              type: 'email',
            }}
          />
        </Col>
        <Col md={4}>
          <Field
            {...{
              label: 'Phone',
              name: 'phone',
              help: 'The contact number of the user',
              component: PhoneNumberInput,
            }}
          />
        </Col>

        <Col md={4}>
          <Field
            onChange
            {...{
              label: 'Time zone',
              name: 'timeZone',
              help: 'The local timezone the user is using. This is use to determine on how to display dates and time on the screen.',
              component: WorldTimeZoneSelect,
            }}
          />
        </Col>
        <Col md={4}>
          <Field
            onChange
            {...{
              label: 'Company',
              name: 'company',
              help: 'The company this user belongs to',
              component: CompanySelectComponent,
            }}
          />
        </Col>
      </Row>
    </FormModal>
  )
}
